import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe2, Heart, Zap, Sparkles, Rocket, X, Gamepad2, ArrowRight } from 'lucide-react';
import { WORLDVIBE_LOGO } from './Logo';
import NeonWordmark from './NeonWordmark';

/**
 * FirstLoginWelcome — a one-shot immersive intro that appears the very first
 * time a user reaches the authenticated shell. It answers the "what is this
 * app?" question in <3 seconds:
 *
 *   1. Big animated brand mark + wordmark (matches the Splash / Landing motion)
 *   2. Punchy one-liner  — "Meet travelers. Match hearts. Rise the ranks."
 *   3. Three parallax "vibe cards"  — chat planets · match hearts · earn XP
 *   4. Neon CTA — dismisses and never shows again for this user
 *
 * Persistence key: `wv_welcome_seen_${user_id}` in localStorage. Guarded per
 * user so switching to a different account on the same device also gets the
 * welcome once.
 */
export default function FirstLoginWelcome({ user, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Trigger entrance on next tick so the CSS transition fires cleanly.
  useEffect(() => {
    const t = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(t);
  }, []);

  const dismiss = () => {
    setLeaving(true);
    try { localStorage.setItem(`wv_welcome_seen_v2_${user?.user_id || 'anon'}`, '1'); } catch { /* ignore */ }
    // Wait for exit transition to finish
    setTimeout(() => onDismiss?.(), 420);
  };

  // Second feature card is age-gated — dating is 18+, so under-18 travelers
  // see a "Play & Win" card instead of the Match Hearts one.
  const isAdult = !!user?.is_adult;
  const nav = useNavigate();

  // ONE recommended first action. Post-login shouldn't feel like a feature
  // brochure — we push the new traveler toward one concrete tap. Planets is
  // the best default because it's where every WorldVibe session naturally
  // begins (chat rooms are the core loop). If we later track "already joined
  // a planet", we can rotate to a different suggestion here.
  const firstAction = {
    icon: Globe2,
    color: '#22D3EE',
    title: 'Join your first planet',
    desc: 'Planets are live themed chat rooms — the fastest way to meet travelers who share your vibe. Pick one that looks fun and dive in.',
    ctaLabel: 'Enter WorldVibe',
    ctaTo: '/app/planets',
  };

  const handlePrimary = () => {
    dismiss();
    nav(firstAction.ctaTo);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to WorldVibe"
      data-testid="first-login-welcome"
      className={`fixed inset-0 z-[100] overflow-hidden ${leaving ? 'pointer-events-none' : ''}`}
      style={{
        background:
          'radial-gradient(circle at 20% 25%, rgba(123,47,247,0.35), transparent 55%),' +
          'radial-gradient(circle at 82% 75%, rgba(0,198,255,0.28), transparent 55%),' +
          'radial-gradient(circle at 50% 100%, rgba(0,245,212,0.20), transparent 65%),' +
          'linear-gradient(180deg,#05050C 0%,#0A0A15 100%)',
        opacity: leaving ? 0 : 1,
        transition: 'opacity 400ms ease',
      }}
    >
      {/* Starfield */}
      <StarField />

      {/* Faint moving grid for depth */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at center, black 40%, transparent 80%)',
          animation: 'wv-grid-drift 40s linear infinite',
        }}
      />

      {/* Close (Skip) */}
      <button
        onClick={dismiss}
        aria-label="Skip intro"
        data-testid="first-login-welcome-skip"
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/8 hover:bg-white/14 border border-white/12 backdrop-blur flex items-center justify-center text-white/70 hover:text-white transition"
      >
        <X size={16} />
      </button>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 py-10 overflow-y-auto">
        {/* Brand mark */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
          style={{ transitionDelay: '80ms' }}
        >
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-3 rounded-3xl blur-2xl opacity-70"
              style={{
                background: 'conic-gradient(from 0deg, #22D3EE, #8B5CF6, #EC4899, #22D3EE)',
                animation: 'wv-halo-spin 8s linear infinite',
              }}
            />
            <img
              src={WORLDVIBE_LOGO}
              alt="WorldVibe"
              className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl object-cover ring-2 ring-white/30"
            />
          </div>
        </div>

        {/* Hi user + wordmark */}
        <div
          className={`mt-5 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
          style={{ transitionDelay: '220ms' }}
        >
          {user?.displayName && (
            <div className="text-xs sm:text-sm uppercase tracking-[0.4em] text-white/50 mb-2 flex items-center justify-center gap-2">
              <Sparkles size={12} className="text-cyan-300" />
              Hey {user.displayName.split(' ')[0]}
              <Sparkles size={12} className="text-fuchsia-300" />
            </div>
          )}
          <h1 className="font-display font-bold leading-tight" data-testid="first-login-welcome-title">
            <span className="block text-2xl sm:text-4xl text-white/95">Welcome to</span>
            <NeonWordmark className="font-display font-bold tracking-tight text-4xl sm:text-6xl leading-none inline-block" animate />
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/70 max-w-md mx-auto">
            One tap to jump in. You can explore everything else once you&apos;re inside.
          </p>
        </div>

        {/* ONE recommended first action — replaces the old 3-card feature
            dump. Goal: give the new traveler exactly one clear next step
            (join a planet) instead of overwhelming them with every feature. */}
        <div
          className={`mt-8 sm:mt-10 w-full max-w-md transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '360ms' }}
        >
          <div
            className="relative rounded-3xl border border-cyan-400/40 bg-gradient-to-br from-cyan-500/15 via-fuchsia-500/8 to-transparent p-5 sm:p-6 backdrop-blur"
            data-testid="first-login-welcome-first-action-card"
            style={{ boxShadow: `0 0 32px ${firstAction.color}22, inset 0 0 24px ${firstAction.color}0f` }}
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold">
              <Sparkles size={11} /> Your first step
            </div>
            <div className="mt-3 flex items-start gap-4">
              <div
                className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: `${firstAction.color}22`, border: `1px solid ${firstAction.color}55`, color: firstAction.color }}
              >
                <firstAction.icon size={26} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-lg sm:text-xl text-white leading-tight">{firstAction.title}</div>
                <p className="text-xs sm:text-sm text-white/70 mt-1 leading-relaxed">{firstAction.desc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Primary CTA — routes to the recommended first action, not just
            dismissal. That way "Enter WorldVibe" isn't a dead-end button
            that dumps them on a dashboard — it takes them straight into
            the chat rooms. Secondary link below lets curious users skip
            to the dashboard if they want to explore. */}
        <div
          className={`mt-8 sm:mt-10 flex flex-col items-center gap-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
          style={{ transitionDelay: '560ms' }}
        >
          <button
            onClick={handlePrimary}
            data-testid="first-login-welcome-cta"
            className="group relative px-8 py-3.5 rounded-full font-display font-bold text-base text-black overflow-hidden transition-transform hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #22D3EE 0%, #8B5CF6 50%, #EC4899 100%)',
              boxShadow: '0 10px 40px -10px rgba(139,92,246,0.6), 0 0 24px rgba(34,211,238,0.4)',
            }}
          >
            <span className="relative flex items-center gap-2">
              <Rocket size={17} /> {firstAction.ctaLabel} <ArrowRight size={15} className="opacity-70" />
            </span>
            <span
              aria-hidden
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: 'linear-gradient(135deg,#EC4899,#22D3EE,#8B5CF6)' }}
            />
          </button>
          <button
            onClick={dismiss}
            data-testid="first-login-welcome-explore-later"
            className="text-xs text-white/50 hover:text-white/80 underline underline-offset-2"
          >
            Skip — I&apos;ll explore later
          </button>
          <div className="text-center text-[11px] text-white/40 tracking-wider mt-1">FREE FOREVER · NO ADS ON CHAT</div>
        </div>
      </div>

      {/* keyframes */}
      <style>{`
        @keyframes wv-grid-drift {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(-48px, -48px); }
        }
        @keyframes wv-halo-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes wv-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}

// A cheap starfield made of 60 randomly-placed spans. Purely decorative;
// pointer-events disabled so it doesn't block the CTA.
function StarField() {
  const stars = React.useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 1 + Math.random() * 2.2,
        delay: Math.random() * 4,
        dur: 3 + Math.random() * 4,
      })),
    []
  );
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      {stars.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animation: `wv-twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}
